// Undo snapshots for Letter Flick.
//
// A snapshot is a *deep enough* copy of the mutable parts of GameState to let
// the controller restore the exact pre-flick position. Captured fields:
//   - cellToToken (Map): rebuilt per snapshot via the Map(iterable) ctor.
//   - tokenToCell (Map): same.
//   - exitedLetters (string[]): cloned with slice() — strings are immutable.
//
// Fields NOT captured: puzzleId, wordLength, letters — those are immutable
// for the life of a GameState (the readonly modifier is enforced at the type
// level and they are never reassigned). status + undoCount are managed by
// the controller, not the snapshot (see restoreSnapshot's note about status).
//
// Restore strategy: mutate the existing Map/array references in place rather
// than replacing them. The fields on GameState are declared `readonly` (the
// reference, not the contents), so swapping the references would be a type
// error AND would invalidate any code that captured the reference. Mutating
// in place keeps every external reference (none today, but defensive) live.

import type { EncodedCell, ExitProvenance, GameState, TokenIdx } from './state'

export interface GameSnapshot {
  readonly cellToToken: Map<EncodedCell, TokenIdx>
  readonly tokenToCell: Map<TokenIdx, EncodedCell>
  readonly exitedLetters: readonly string[]
  // Length-parallel to exitedLetters. Captured + restored together so the
  // bar's per-slot provenance (used for green-vs-orange highlighting in
  // render.ts) stays in sync with the bar letters across undo. Without this
  // an undo would leave a stale provenance entry for the popped slot,
  // mis-coloring it on the next flick that re-fills it.
  readonly exitedProvenance: readonly ExitProvenance[]
  // Captured so undo restores the pre-flick flickCount. Without this an undo
  // would leave flickCount inflated by the now-undone flick, which would
  // corrupt the eventual end-of-game stats + share grid.
  readonly flickCount: number
}

export function cloneSnapshot(state: GameState): GameSnapshot {
  return {
    cellToToken: new Map(state.cellToToken),
    tokenToCell: new Map(state.tokenToCell),
    exitedLetters: state.exitedLetters.slice(),
    exitedProvenance: state.exitedProvenance.slice(),
    flickCount: state.flickCount,
  }
}

// Restore `target` to the position captured in `snapshot`. Mutates in place;
// does NOT reassign target's fields (they are readonly references). Also
// resets `target.status` to 'playing' — an undo always returns the player to
// active play. `target.undoCount` is intentionally NOT touched here; the
// caller increments it (the snapshot doesn't know how many undos have been
// used before/after it was captured).
export function restoreSnapshot(target: GameState, snapshot: GameSnapshot): void {
  target.cellToToken.clear()
  for (const [cell, tokenIdx] of snapshot.cellToToken) {
    target.cellToToken.set(cell, tokenIdx)
  }
  target.tokenToCell.clear()
  for (const [tokenIdx, cell] of snapshot.tokenToCell) {
    target.tokenToCell.set(tokenIdx, cell)
  }
  target.exitedLetters.length = 0
  for (const letter of snapshot.exitedLetters) {
    target.exitedLetters.push(letter)
  }
  target.exitedProvenance.length = 0
  for (const prov of snapshot.exitedProvenance) {
    target.exitedProvenance.push(prov)
  }
  target.flickCount = snapshot.flickCount
  target.status = 'playing'
}

// JSON-serializable shape for persistence. Maps become entry arrays; the rest
// passes through. Used by persistence.ts to round-trip the undo stack across
// page reloads so Safari refresh mid-puzzle doesn't strand the player with
// no usable undos.
export interface SerializedSnapshot {
  readonly cellToToken: ReadonlyArray<readonly [EncodedCell, TokenIdx]>
  readonly tokenToCell: ReadonlyArray<readonly [TokenIdx, EncodedCell]>
  readonly exitedLetters: readonly string[]
  readonly exitedProvenance: readonly ExitProvenance[]
  readonly flickCount: number
}

export function serializeSnapshot(snapshot: GameSnapshot): SerializedSnapshot {
  return {
    cellToToken: Array.from(snapshot.cellToToken),
    tokenToCell: Array.from(snapshot.tokenToCell),
    exitedLetters: snapshot.exitedLetters.slice(),
    exitedProvenance: snapshot.exitedProvenance.slice(),
    flickCount: snapshot.flickCount,
  }
}

export function deserializeSnapshot(serialized: SerializedSnapshot): GameSnapshot {
  return {
    cellToToken: new Map(serialized.cellToToken),
    tokenToCell: new Map(serialized.tokenToCell),
    exitedLetters: serialized.exitedLetters.slice(),
    exitedProvenance: serialized.exitedProvenance.slice(),
    flickCount: serialized.flickCount,
  }
}

// Defensive validation used by persistence.ts at parse time. Returns true
// for an object whose shape matches SerializedSnapshot. Inner per-entry
// validation is intentionally light — the field types are primitive numbers
// and strings; deeper checks would only catch hand-edited localStorage.
export function isValidSerializedSnapshot(value: unknown): value is SerializedSnapshot {
  if (value === null || typeof value !== 'object') return false
  const o = value as Record<string, unknown>
  if (!Array.isArray(o['cellToToken'])) return false
  if (!Array.isArray(o['tokenToCell'])) return false
  if (!Array.isArray(o['exitedLetters'])) return false
  if (!Array.isArray(o['exitedProvenance'])) return false
  if (typeof o['flickCount'] !== 'number') return false
  return true
}
